import { ChangeDetectionStrategy, Component, Signal, signal, ViewChild } from '@angular/core';
import { MakeCallForm } from './make-call.form';
import { MatSnackBar } from '@angular/material/snack-bar';
import { post } from 'aws-amplify/api';
import { CallInterface, CommunicationDirectionMap, MakeCallPayload } from '@sko/models';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { PhoneNumberOption } from '../select-phone-number';
import { FormGroupDirective } from '@angular/forms';
import { LoggerService } from '@sko/ng-logging';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-make-call',
    templateUrl: './make-call.component.html',
    styleUrl: './make-call.component.sass',
    standalone: false
})
export class MakeCallComponent {

  form = new MakeCallForm();

  @ViewChild(FormGroupDirective) formGroup: FormGroupDirective;

  from: Signal<string>;

  private fromHintSignal = signal<string>('');

  fromHint = this.fromHintSignal.asReadonly();

  private processingSignal = signal<boolean>(false);

  processing = this.processingSignal.asReadonly();

  private resultSignal = signal<CallInterface | undefined>(undefined);

  result = this.resultSignal.asReadonly();

  private toHintSignal = signal<string>('');

  toHint = this.toHintSignal.asReadonly();

  private viaHintSignal = signal<string>('');

  viaHint = this.viaHintSignal.asReadonly();

  constructor(
    private logger: LoggerService,
    private snackbar: MatSnackBar,
  ) { 

    this.from = toSignal(this.form.valueChanges.pipe(map((value) => value.from)), {
      initialValue: this.form.value.from,
    });

  }

  onFromSelected(option: PhoneNumberOption) {

    this.form.patchValue({
      from: option.phone_number,
    });

    this.fromHintSignal.set(option.label);

  }

  onToSelected(option: PhoneNumberOption) {

    this.form.patchValue({
      announce: `Now calling ${option.label}`,
      to: option.phone_number,
    });

    this.toHintSignal.set(option.label);

  }

  onViaSelected(option: PhoneNumberOption) {

    this.form.patchValue({
      via: option.phone_number,
    });

    this.viaHintSignal.set(option.label);

  }

  submit() {

    if (this.form.valid) {

      this.processingSignal.set(true);

      let announce = this.form.value.announce;

      if (typeof announce !== 'string' || announce === '') {

        announce = this.form.value.to;

      }

      const body: Omit<MakeCallPayload, 'call_sid' | 'epoch' | 'id'> = {
        announce,
        direction: CommunicationDirectionMap.OUTBOUND,
        from: this.form.value.from,
        record: this.form.value.record,
        to: this.form.value.to,
        via: this.form.value.via,
      };

      post({
        apiName: 'default',
        path: '/calls/make',
        options: {
          body,
        },
      })
      .response
      .then((response) => {

        return response.body.json();

      })
      .then((payload) => {

        const result = payload as unknown as CallInterface;

        this.resultSignal.set(result);

        this.form.reset();

        this.formGroup.resetForm();

        this.snackbar.open(`Call Made From: ${result.from} To: ${result.to} Via: ${result.via}`, 'Dismiss');

      })
      .catch((e) => {

        this.logger.error(e);

      })
      .finally(() => {

        this.processingSignal.set(false);

      });

    } else {

      this.snackbar.open('The form is not valid. Check for errors and try again.', 'Dismiss');

    }

  }

}
